import { useState } from 'react';
import { createStore, createHook, type StoreActionApi } from 'react-sweet-state';
import log from '@atlassian/jira-common-util-logging/src/log';
import type { Status, State } from './types';

const PACKAGE_LOCATION = 'jira-business.common.services.global-statuses';
const MAX_RETRIES = 3;

const set =
	(statuses: Status[]) =>
	({ setState }: StoreActionApi<State>) =>
		setState({
			statuses,
		});

const actions = {
	set,
} as const;

type Actions = typeof actions;

const GlobalStatusesStore = createStore<State, Actions>({
	initialState: { statuses: [] },
	actions,
	name: 'globalStatuses',
});

export const useGlobalStatusesStore = createHook(GlobalStatusesStore);

const emptyArray: Array<Status> = [];

export const useGlobalStatuses = (): {
	statuses: Status[];
	loading: boolean;
} => {
	const [isLoading, setIsLoading] = useState(false);
	const [hasLoaded, setHasLoaded] = useState(false);
	const [retriesCount, setRetriesCount] = useState(0);
	const [state, statusActions] = useGlobalStatusesStore();

	const retry = () => {
		setRetriesCount(retriesCount + 1);
		setIsLoading(false);
		setHasLoaded(false);
	};

	if (Array.isArray(state.statuses) && state.statuses.length > 0) {
		return { statuses: state.statuses, loading: isLoading };
	}

	if (!isLoading && !hasLoaded) {
		fetch('/rest/api/3/status')
			.then((response) => response.json())
			.then((statuses: Status[]) => {
				statusActions.set(statuses);
				setIsLoading(false);
				setHasLoaded(true);
			})
			.catch(() => {
				log.safeErrorWithoutCustomerData(
					PACKAGE_LOCATION,
					`Failed to retrieve global statuses - Retry #${retriesCount}`,
				);

				if (retriesCount < MAX_RETRIES) {
					retry();
				}
			});
		setIsLoading(true);
	}

	return { statuses: emptyArray, loading: isLoading };
};
